// When the hard paywall covers the app, and how an offer is worded — ported from the iOS PaywallPolicy and the offer
// wording in TrailBivvy/Features/Paywall/PaywallPresentation.swift (iOS DECISION.md S2, S4, S7, S14).

import type { Access, SubscriptionDetails } from './entitlement';

export type PaywallPhase = 'checking' | 'locked';

/** Longest time a recording or navigation running when access ended keeps the paywall back (iOS S4/S14). */
export const activityHoldLimit = 24 * 3_600_000;

/** The paywall covers the app without access, once the onboarding is done, unless a live activity holds it. */
export function paywallPhase(access: Access, onboardingCompleted: boolean, activityHoldsPaywall: boolean): PaywallPhase | undefined {
  if (!onboardingCompleted || activityHoldsPaywall) return undefined;
  if (access === 'entitled') return undefined;
  return access === 'checking' ? 'checking' : 'locked';
}

/**
 * Hold deadline after access changed from `old` to `next`: a hold starts only when access is lost while a live
 * activity runs, and lasts `activityHoldLimit`; access coming back clears it.
 */
export function holdAfterAccessChange(old: Access, next: Access, liveActivity: boolean, current: number | undefined, now: number) {
  if (next === 'entitled') return undefined;
  if (old === 'entitled' && liveActivity) return now + activityHoldLimit;
  return current;
}

export function activityHolds(liveActivity: boolean, holdUntil: number | undefined, now: number): boolean {
  return liveActivity && holdUntil !== undefined && now < holdUntil;
}

/** Price of the plan for display: "CHF 18.00", "22,99 €"… (the locale's own currency format). */
export function formatPrice(unitAmount: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(unitAmount / 100);
}

/** "3 days", "3 jours", "3 Tage" — the system's own wording of the trial length (iOS S7). */
export function formatTrialLength(days: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'unit', unit: 'day', unitDisplay: 'long' }).format(days);
}

/** Status line of More › TrailBivvy Pro (iOS S8), as a key and the date it names. */
export type StatusLine =
  | { key: 'trialRenews' | 'trialEnds' | 'activeRenews' | 'activeEnds'; date: number }
  | { key: 'billingProblem' | 'renewalUnconfirmed' | 'ended' };

export function statusLine(details: SubscriptionDetails | undefined): StatusLine | undefined {
  if (!details) return undefined;
  switch (details.status) {
    case 'active':
      if (details.isFreeTrial) return { key: details.willAutoRenew ? 'trialRenews' : 'trialEnds', date: details.periodEndsAt };
      return { key: details.willAutoRenew ? 'activeRenews' : 'activeEnds', date: details.periodEndsAt };
    case 'gracePeriod':
    case 'billingRetry':
      return { key: 'billingProblem' };
    case 'renewalUnconfirmed':
      return { key: 'renewalUnconfirmed' };
    case 'expired':
    case 'revoked':
      return { key: 'ended' };
  }
}
